/**
 * <circle-media> — the left panel of the Circle accordion.
 *
 * The cross-fade itself is pure CSS: `:has(.circle-row:nth-of-type(N)[open])` raises the matching
 * layer's opacity, so the reveal works with JavaScript disabled. This element exists only to start
 * and stop the videos, which CSS cannot do.
 *
 * The videos are `preload="none"` with no `autoplay` attribute, so a row nobody has opened costs
 * nothing: no download, no decode, no wasted battery on eight simultaneous loops.
 *
 * A custom element rather than an inline <script>, because the Section Rendering API re-injects
 * section HTML on every theme-editor change and never re-executes script tags.
 */
// Wrapped in an IIFE because a section can appear twice on one page (rail.liquid does),
// which emits this script twice; a second top-level `class` declaration is a hard error.
(() => {
class CircleMedia extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('details.circle-row'));
    if (!this.rows.length) return;

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.sync = this.sync.bind(this);

    // `toggle` fires on the row that opened AND on the one the browser closed for it (the shared
    // `name` makes them exclusive), so a single listener per row is enough.
    this.rows.forEach((row) => row.addEventListener('toggle', this.sync));
    this.motion.addEventListener('change', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    this.rows?.forEach((row) => row.removeEventListener('toggle', this.sync));
    this.motion?.removeEventListener('change', this.sync);
  }

  sync() {
    const open = this.rows.findIndex((row) => row.open);

    this.querySelectorAll('.circle-media__video').forEach((video) => {
      if (!video.paused) video.pause();
    });

    // Reduced motion keeps the poster frame, which is the row's own image.
    if (open < 0 || this.motion.matches) return;

    const video = this.querySelector(`.circle-media__layer[data-row="${open + 1}"] video`);
    // play() rejects when the tab is backgrounded or the source is missing. Neither is worth a throw.
    video?.play().catch(() => {});
  }
}

if (!customElements.get('circle-media')) customElements.define('circle-media', CircleMedia);
})();
